import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, FormControlLabel, IconButton, Link as MuiLink, List,
  ListItem, ListItemButton, ListItemIcon, ListItemText, MenuItem, Paper, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CloseIcon from '@mui/icons-material/Close';

import { useAuth } from '../../providers/AuthProvider';
import { selectUserInfos } from '../../store/userDataStore';
import { formatDateFull } from '../../misc/dateFormat';
import { codeReviewService, CodeBranch, CodeFile, CodeReviewGroup } from '../../services/codeReviewService';
import CodeView from './CodeView';
import {
  DEFAULT_BRANCH, LineRange, blobUrl, commitsUrl, defaultFile, graphUrl, languageForPath, newIssueUrl, treeRows,
} from './codeReviewUi';

const FONT_SIZE = 13;
const PROJECTION_FONT_SIZE = 20;

// Donnée chargée pour une clé (groupe, commit, fichier) : jamais affichée sous une autre clé, ce qui évite
// de montrer un instant le code du groupe précédent pendant un changement de groupe
interface Keyed<T> {
  key: string;
  value: T;
}

const groupLabel = (group: CodeReviewGroup) => {
  const names = group.members.map(m => m.lastname).filter(Boolean).join(', ');
  return names ? `${group.name} — ${names}` : group.name;
};

// Revue de code (enseignant·es) : code des dépôts GitLab des groupes, en lecture seule. Le fichier ouvert
// reste le même en passant d'un groupe à l'autre ; les remarques se font dans GitLab (issue pré-remplie).
const CodeReviewPage: React.FC = () => {
  const { getValidToken } = useAuth();
  const userInfos = useSelector(selectUserInfos);
  const isTeacher = userInfos?.access === 'teacher' || userInfos?.access === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();

  const [groups, setGroups] = useState<CodeReviewGroup[]>([]);
  const [groupId, setGroupId] = useState<number | null>(() => Number(searchParams.get('groupe')) || null);
  const [branches, setBranches] = useState<Keyed<CodeBranch[]> | null>(null);
  // Branche et fichier demandés : conservés d'un groupe à l'autre, même quand un dépôt ne les a pas
  const [wantedBranch, setWantedBranch] = useState(() => searchParams.get('branche') || DEFAULT_BRANCH);
  const [wantedPath, setWantedPath] = useState<string | null>(() => searchParams.get('fichier'));
  const [tree, setTree] = useState<Keyed<string[]> | null>(null);
  const [file, setFile] = useState<Keyed<CodeFile> | null>(null);
  const [selection, setSelection] = useState<LineRange | null>(null);
  const [projection, setProjection] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filtre groupe TD ('' = tous) : limite le sélecteur et les flèches précédent / suivant
  const [tdFilter, setTdFilter] = useState('');
  const tdGroups = useMemo(() => [...new Set(groups.map(g => g.td_group).filter((td): td is string => !!td))], [groups]);
  const visibleGroups = tdFilter ? groups.filter(g => g.td_group === tdFilter) : groups;

  const group = groups.find(g => g.id === groupId) ?? null;
  const groupIndex = group ? visibleGroups.indexOf(group) : -1;
  const webUrl = group?.gitlab_web_url ?? null;
  const groupBranches = branches?.key === String(groupId) ? branches.value : null;
  const branch = groupBranches
    ? groupBranches.find(b => b.name === wantedBranch) ?? groupBranches.find(b => b.name === DEFAULT_BRANCH) ?? groupBranches[0] ?? null
    : null;
  const treeKey = branch ? `${groupId}@${branch.sha}` : '';
  const files = tree?.key === treeKey ? tree.value : null;
  const path = files ? (wantedPath && files.includes(wantedPath) ? wantedPath : defaultFile(files)) : null;
  const fileKey = path ? `${treeKey}:${path}` : '';
  const currentFile = file?.key === fileKey ? file.value : null;

  const rows = useMemo(() => (files ? treeRows(files) : []), [files]);
  const code = useMemo(
    () => (currentFile?.content !== undefined ? currentFile.content.replace(/\r\n?/g, '\n').replace(/\n$/, '') : null),
    [currentFile]
  );
  const lines = useMemo(() => (code !== null ? code.split('\n') : []), [code]);

  // Appel d'API avec un token valide ; la réponse est ignorée si l'effet a été annulé entre-temps
  const load = async <T,>(call: (token: string) => Promise<T>, apply: (value: T) => void, isCancelled: () => boolean) => {
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      const value = await call(token);
      if (!isCancelled()) apply(value);
    } catch (e) {
      if (!isCancelled()) setError((e instanceof Error && e.message) || 'Erreur lors de la lecture du dépôt');
    }
  };

  useEffect(() => {
    if (!isTeacher) return;
    let cancelled = false;
    load(token => codeReviewService.listGroups(token), data => {
      setGroups(data);
      // Groupe demandé inconnu (ou aucun) : le premier groupe réel, les bacs à sable seulement à défaut
      setGroupId(current => (data.some(g => g.id === current) ? current : (data.find(g => !g.is_sandbox) ?? data[0])?.id ?? null));
    }, () => cancelled);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeacher]);

  useEffect(() => {
    if (groupId === null) return;
    let cancelled = false;
    setError(null);
    load(token => codeReviewService.listBranches(groupId, token), value => setBranches({ key: String(groupId), value }), () => cancelled);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  useEffect(() => {
    if (groupId === null || !branch) return;
    let cancelled = false;
    const key = treeKey;
    setError(null);
    load(token => codeReviewService.listFiles(groupId, branch.sha, token), value => setTree({ key, value }), () => cancelled);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeKey]);

  useEffect(() => {
    setSelection(null);
    if (groupId === null || !branch || !path) return;
    let cancelled = false;
    const key = fileKey;
    setError(null);
    load(token => codeReviewService.getFile(groupId, branch.sha, path, token), value => setFile({ key, value }), () => cancelled);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileKey]);

  // Groupe, branche et fichier dans l'URL : un lien copié rouvre la même vue
  useEffect(() => {
    const params: Record<string, string> = { branche: wantedBranch };
    if (groupId !== null) params.groupe = String(groupId);
    if (wantedPath) params.fichier = wantedPath;
    const next = new URLSearchParams(params);
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, wantedBranch, wantedPath]);

  // Changement de TD : si le groupe affiché n'en fait pas partie, on passe au premier groupe de ce TD
  const handleTdFilterChange = (td: string) => {
    setTdFilter(td);
    const candidates = td ? groups.filter(g => g.td_group === td) : groups;
    if (!candidates.some(g => g.id === groupId)) {
      setGroupId((candidates.find(g => !g.is_sandbox) ?? candidates[0])?.id ?? null);
    }
  };

  const handleLineClick = (line: number, extend: boolean) => {
    setSelection(current => {
      if (extend && current) return { start: Math.min(current.start, line), end: Math.max(current.end, line) };
      if (current && current.start === line && current.end === line) return null;
      return { start: line, end: line };
    });
  };

  if (!isTeacher) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Revue de code</Typography>
        <Alert severity="info">Page réservée aux enseignant·es.</Alert>
      </Box>
    );
  }

  return (
    // Hauteur de la fenêtre moins les marges de AppLayout : le panneau du code prend toute la place restante
    <Box sx={{ p: 3, height: 'calc(100vh - 72px)', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Revue de code</Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
        <TextField
          select
          size="small"
          label="Groupe TD"
          sx={{ minWidth: 140, mr: 1 }}
          value={tdFilter}
          onChange={e => handleTdFilterChange(e.target.value)}
        >
          <MenuItem value="">Tous</MenuItem>
          {tdGroups.map(td => <MenuItem key={td} value={td}>{td}</MenuItem>)}
        </TextField>
        <Tooltip title="Groupe précédent">
          <span>
            <IconButton disabled={groupIndex <= 0} onClick={() => setGroupId(visibleGroups[groupIndex - 1].id)}>
              <NavigateBeforeIcon />
            </IconButton>
          </span>
        </Tooltip>
        {projection ? (
          // Mode projection : aucun nom à l'écran, le code peut être montré à toute la promo
          <Typography sx={{ width: 520, textAlign: 'center', fontWeight: 'bold' }}>
            {group ? `Groupe ${groupIndex + 1} / ${visibleGroups.length}` : '—'}
          </Typography>
        ) : group ? (
          <Autocomplete
            sx={{ width: 520 }}
            size="small"
            options={visibleGroups}
            // Liste déroulante plus haute que celle par défaut de MUI (40vh)
            slotProps={{ listbox: { sx: { maxHeight: '60vh' } } }}
            groupBy={g => g.td_group || 'Sans TD'}
            getOptionLabel={groupLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={group}
            onChange={(_, value) => setGroupId(value.id)}
            disableClearable
            renderInput={params => <TextField {...params} label="Groupe" />}
          />
        ) : (
          <TextField size="small" label="Groupe" disabled sx={{ width: 520 }} />
        )}
        <Tooltip title="Groupe suivant">
          <span>
            <IconButton
              disabled={groupIndex < 0 || groupIndex >= visibleGroups.length - 1}
              onClick={() => setGroupId(visibleGroups[groupIndex + 1].id)}
            >
              <NavigateNextIcon />
            </IconButton>
          </span>
        </Tooltip>
        <TextField
          select
          size="small"
          label="Branche"
          sx={{ minWidth: 200, ml: 1 }}
          value={branch?.name ?? ''}
          disabled={!groupBranches?.length}
          onChange={e => setWantedBranch(e.target.value)}
        >
          {(groupBranches ?? []).map(b => <MenuItem key={b.name} value={b.name}>{b.name}</MenuItem>)}
        </TextField>
        <FormControlLabel
          sx={{ ml: 1 }}
          control={<Switch checked={projection} onChange={e => setProjection(e.target.checked)} />}
          label="Mode projection"
        />
      </Box>

      {branch && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Dernier commit : « {branch.last_commit_title} »
          {!projection && branch.last_commit_author ? ` par ${branch.last_commit_author}` : ''}
          {branch.last_commit_date ? `, le ${formatDateFull(branch.last_commit_date)}` : ''}
          {webUrl && (
            <>
              {' · '}
              <MuiLink href={commitsUrl(webUrl, branch.name)} target="_blank" rel="noopener">historique</MuiLink>
              {' · '}
              <MuiLink href={graphUrl(webUrl, branch.name)} target="_blank" rel="noopener">graphe des branches</MuiLink>
            </>
          )}
        </Typography>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {files && wantedPath && path !== wantedPath && (
        <Alert severity="info" sx={{ mb: 2 }}>« {wantedPath} » n'existe pas dans ce dépôt sur cette branche.</Alert>
      )}

      <Paper variant="outlined" sx={{ display: 'flex', flex: 1, minHeight: 360 }}>
        <Box sx={{ width: 260, flexShrink: 0, borderRight: 1, borderColor: 'divider', overflowY: 'auto' }}>
          {files === null ? (
            !error && groupId !== null && <Box sx={{ p: 2 }}><CircularProgress size={24} /></Box>
          ) : (
            <List dense disablePadding>
              {rows.map(row => (row.kind === 'dir' ? (
                <ListItem key={`dir:${row.path}`} sx={{ pl: 1 + row.depth * 2, py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><FolderOutlinedIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={row.name} slotProps={{ primary: { variant: 'body2', color: 'text.secondary' } }} />
                </ListItem>
              ) : (
                <ListItemButton
                  key={row.path}
                  selected={row.path === path}
                  onClick={() => setWantedPath(row.path)}
                  sx={{ pl: 1 + row.depth * 2, py: 0.25 }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}><InsertDriveFileOutlinedIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={row.name} slotProps={{ primary: { variant: 'body2', noWrap: true } }} />
                </ListItemButton>
              )))}
            </List>
          )}
        </Box>

        {/* contain : une ligne de code très longue défile dans le panneau au lieu d'élargir toute la page */}
        <Box sx={{ flex: 1, minWidth: 0, contain: 'inline-size', display: 'flex', flexDirection: 'column' }}>
          {path && branch && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{path}</Typography>
              {webUrl && (
                <Tooltip title="Ouvrir ce fichier dans GitLab (version de ce commit)">
                  <IconButton size="small" href={blobUrl(webUrl, branch.sha, path)} target="_blank" rel="noopener">
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Box sx={{ flex: 1 }} />
              {selection && webUrl && group ? (
                <>
                  <Typography variant="body2">
                    {selection.start === selection.end ? `Ligne ${selection.start}` : `Lignes ${selection.start} à ${selection.end}`}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                    href={blobUrl(webUrl, branch.sha, path, selection)}
                    target="_blank"
                    rel="noopener"
                  >
                    Voir dans GitLab
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<RateReviewIcon />}
                    href={newIssueUrl({ group, webUrl, branch: branch.name, sha: branch.sha, path, range: selection, lines })}
                    target="_blank"
                    rel="noopener"
                  >
                    Remarque (issue GitLab)
                  </Button>
                  <Tooltip title="Désélectionner">
                    <IconButton size="small" onClick={() => setSelection(null)}><CloseIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Clic sur un numéro de ligne (Maj+clic pour plusieurs lignes) pour faire une remarque
                </Typography>
              )}
            </Box>
          )}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {currentFile === null ? (
              path && !error && <Box sx={{ p: 2 }}><CircularProgress size={24} /></Box>
            ) : code !== null ? (
              <CodeView
                code={code}
                language={languageForPath(currentFile.path)}
                fontSize={projection ? PROJECTION_FONT_SIZE : FONT_SIZE}
                selection={selection}
                onLineClick={handleLineClick}
              />
            ) : (
              <Alert severity="info" sx={{ m: 2 }}>
                {currentFile.binary ? "Fichier binaire : pas d'aperçu ici." : 'Fichier trop volumineux pour être affiché ici.'}
              </Alert>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default CodeReviewPage;
